import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Download, BookOpen, Video, FileText, Users, Calendar, CheckCircle, ArrowRight, Clock, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';

const IMAGES = {
  gettingStarted: '/assets/getting-started.png',
  leaveRequest: '/assets/leave-request.png',
  adminDashboard: '/assets/admin-dashboard.png',
  classroom: 'https://images.pexels.com/photos/3231358/pexels-photo-3231358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  analytics: '/assets/analytics-illustration.png',
  notification: '/assets/notification.png'
};

export const Tutorials = () => {
  const [downloading, setDownloading] = useState(false);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDownloadPDF = () => {
    setDownloading(true);
    window.open('/docs/Ariskool_Complete_Guide.html', '_blank');
    toast.success('Opening complete guide in new tab!');
    setTimeout(() => setDownloading(false), 1000);
  };

  const tutorials = [
    {
      category: 'Getting Started',
      icon: BookOpen,
      image: IMAGES.gettingStarted,
      items: [
        {
          title: 'Quick Start Guide',
          duration: '5 min',
          description: 'Get up and running in minutes',
          image: IMAGES.gettingStarted,
          steps: [
            { step: 'Visit Ariskool', detail: 'Go to the Ariskool website and click "Get Started" to begin registration.' },
            { step: 'School Details', detail: 'Enter your school name, admin email, select your State, District, and School Board (CBSE, ICSE, State Board, etc.).' },
            { step: 'Set Password', detail: 'Create a secure password (minimum 8 characters). Use a mix of letters, numbers, and symbols.' },
            { step: 'Dashboard Access', detail: 'After registration, you\'ll be redirected to your admin dashboard with leave statistics.' },
            { step: 'Add Teachers', detail: 'Navigate to "Users" to start adding your teaching staff with roles and departments.' }
          ],
          tip: 'Use your official school email domain for better organization and trust.'
        },
        {
          title: 'School Setup',
          duration: '10 min',
          description: 'Configure your school settings',
          image: IMAGES.adminDashboard,
          steps: [
            { step: 'Set Timezone', detail: 'Configure your school\'s timezone (default: Asia/Kolkata) and working hours.' },
            { step: 'Add Holidays', detail: 'Input public holidays, school-specific holidays, and exam periods.' },
            { step: 'Leave Policies', detail: 'Configure leave types: Sick (12 days), Casual (10 days), Vacation (20 days). Customize as needed.' },
            { step: 'Departments', detail: 'Create departments (Maths, Science, English, Hindi, etc.) to organize your staff.' },
            { step: 'Approval Workflow', detail: 'Set up who can approve leaves — Admin only, or HR + Admin chain.' }
          ],
          tip: 'Start with default policies and customize later based on your school\'s needs.'
        },
        {
          title: 'Adding Teachers',
          duration: '8 min',
          description: 'Invite and manage staff members',
          image: IMAGES.notification,
          steps: [
            { step: 'Open Users', detail: 'Navigate to the "Users" section in the dashboard sidebar.' },
            { step: 'Click Add User', detail: 'Enter: Full Name, Email, Role (Teacher/HR/Admin), Department, Employee ID.' },
            { step: 'System Password', detail: 'The system generates a temporary password for the new user.' },
            { step: 'Share Credentials', detail: 'Share the login credentials securely with the teacher via email or in person.' },
            { step: 'First Login', detail: 'Teacher logs in and changes their temporary password to a secure one.' }
          ],
          tip: 'For bulk onboarding, you can import multiple teachers via CSV file.'
        }
      ]
    },
    {
      category: 'For Teachers',
      icon: Users,
      image: IMAGES.leaveRequest,
      items: [
        {
          title: 'Applying for Leave',
          duration: '3 min',
          description: 'Submit leave requests easily',
          image: IMAGES.leaveRequest,
          steps: [
            { step: 'My Leaves', detail: 'Click "My Leaves" in the sidebar to see your leave dashboard.' },
            { step: 'New Request', detail: 'Click "New Leave Request" button to start a new application.' },
            { step: 'Fill Details', detail: 'Select leave type, choose start/end dates, enter reason for leave.' },
            { step: 'Attach Documents', detail: 'Upload medical certificate or other supporting documents if required.' },
            { step: 'Submit', detail: 'Review your request and click "Submit". You\'ll get a confirmation notification.' }
          ],
          tip: 'Apply at least 3 days in advance for planned leaves. For emergencies, apply as soon as possible.'
        },
        {
          title: 'Tracking Leave Balance',
          duration: '4 min',
          description: 'Monitor your available leaves',
          image: IMAGES.analytics,
          steps: [
            { step: 'Dashboard View', detail: 'Your leave balance summary is visible on the main dashboard.' },
            { step: 'Detailed Balance', detail: 'View breakdown by leave type: Sick, Casual, Vacation, etc.' },
            { step: 'Used vs Available', detail: 'See Total Allocated, Used, Pending, and Available days for each type.' },
            { step: 'Carry Forward', detail: 'Check if unused leaves carry to next year (up to 5 days per policy).' }
          ],
          tip: 'Formula: Available = Total Allocated - Used - Pending. Plan your leaves accordingly.'
        },
        {
          title: 'Viewing Leave Status',
          duration: '2 min',
          description: 'Check approval status in real-time',
          image: IMAGES.notification,
          steps: [
            { step: 'Notification Bell', detail: 'Check the notification bell in the sidebar for real-time updates.' },
            { step: 'Leave List', detail: 'Go to "My Leaves" to see all your requests with status badges.' },
            { step: 'Status Colors', detail: 'Pending (amber), Approved (green), Rejected (red) — instant visual feedback.' },
            { step: 'Rejection Reason', detail: 'If rejected, click the request to see the admin\'s reason. You can re-apply.' }
          ],
          tip: 'You\'ll receive in-app notifications instantly when your leave status changes.'
        }
      ]
    },
    {
      category: 'For Admins',
      icon: CheckCircle,
      image: IMAGES.adminDashboard,
      items: [
        {
          title: 'Approving Leave Requests',
          duration: '5 min',
          description: 'Review and approve applications',
          image: IMAGES.adminDashboard,
          steps: [
            { step: 'Leave Requests', detail: 'Navigate to "Leave Requests" in the sidebar to see all pending requests.' },
            { step: 'Filter & Sort', detail: 'Filter by status (Pending, Approved, Rejected) to manage efficiently.' },
            { step: 'Review Details', detail: 'Click a request to see teacher info, leave type, duration, reason, and documents.' },
            { step: 'Check Balance', detail: 'Verify the teacher has sufficient leave balance before approving.' },
            { step: 'Approve/Reject', detail: 'Click "Approve" or "Reject" with optional comments. The teacher gets notified instantly.' }
          ],
          tip: 'Respond within 48 hours. Check department staffing before approving long leaves.'
        },
        {
          title: 'Managing Leave Policies',
          duration: '7 min',
          description: 'Configure leave types and rules',
          image: IMAGES.gettingStarted,
          steps: [
            { step: 'Settings', detail: 'Go to Settings > Leave Policies to view and edit all leave types.' },
            { step: 'Edit Allocations', detail: 'Set days per year, carry-forward rules, and max carry limits for each type.' },
            { step: 'Custom Types', detail: 'Create custom leaves: Bereavement, Study Leave, Compensatory Off, Marriage Leave.' },
            { step: 'Document Rules', detail: 'Set which leave types require mandatory document uploads (e.g., medical certificates).' },
            { step: 'Notice Period', detail: 'Configure minimum notice period for each leave type (e.g., 3 days for vacation).' }
          ],
          tip: 'Start with standard policies and customize gradually based on school requirements.'
        },
        {
          title: 'Generating Reports',
          duration: '6 min',
          description: 'Analytics and insights',
          image: IMAGES.analytics,
          steps: [
            { step: 'Analytics Tab', detail: 'Navigate to the Analytics section from the sidebar.' },
            { step: 'Choose Report', detail: 'Select: Leave Summary, Balance Report, Attendance, Trend Analysis, or Department Report.' },
            { step: 'Date Range', detail: 'Pick a date range — monthly, quarterly, or custom.' },
            { step: 'View Charts', detail: 'Interactive charts show leave trends, patterns, and department comparisons.' },
            { step: 'Export', detail: 'Export reports as PDF, Excel, or CSV for board meetings and documentation.' }
          ],
          tip: 'Monthly reports help identify patterns — plan substitute teachers during peak leave months.'
        }
      ]
    }
  ];

  const videoTutorials = [
    { title: 'Platform Overview', duration: '5:30', description: 'Complete walkthrough of Ariskool features', thumbnail: IMAGES.gettingStarted },
    { title: 'Teacher Onboarding', duration: '8:15', description: 'How to add and manage teaching staff', thumbnail: IMAGES.classroom },
    { title: 'Admin Dashboard Tour', duration: '10:00', description: 'Master the analytics and approval workflow', thumbnail: IMAGES.adminDashboard },
  ];

  const handleTutorialClick = (catIdx, itemIdx) => {
    setSelectedTutorial(tutorials[catIdx].items[itemIdx]);
    setDialogOpen(true);
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white py-0 px-0" data-testid="tutorials-page">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <div className="py-24 px-6 bg-slate-900 relative overflow-hidden -mx-0" style={{ marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)', paddingLeft: 'calc(50vw - 50%)', paddingRight: 'calc(50vw - 50%)' }}>
            <div className="absolute top-[-100px] left-[40%] w-[500px] h-[500px] rounded-full bg-teal-500/10 blur-[120px] pointer-events-none" />
            <div className="text-center relative z-10">
              <p className="text-teal-400 text-sm font-semibold uppercase tracking-wider mb-4">Tutorials</p>
              <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" data-testid="tutorials-title">
                Learn Ariskool
              </h1>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                Step-by-step visual guides to help you master leave management for your school
              </p>
            </div>
          </div>

          {/* Download PDF */}
          <Card className="mb-12 border border-slate-200 overflow-hidden shadow-sm mt-12" data-testid="pdf-download-card">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/3 h-48 md:h-auto">
                <img src={IMAGES.gettingStarted} alt="User Guide" className="w-full h-full object-cover" />
              </div>
              <CardContent className="flex-1 py-8 flex flex-col justify-center">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Complete User Guide & Training Manual</h3>
                    <p className="text-slate-600 mb-3">Illustrated guide with step-by-step instructions, screenshots, and best practices for schools</p>
                    <div className="flex flex-wrap gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-emerald-500" /> Visual walkthroughs</span>
                      <span className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-emerald-500" /> Illustrated examples</span>
                      <span className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-emerald-500" /> Indian school context</span>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="bg-teal-600 hover:bg-teal-500 text-white h-14 px-8 whitespace-nowrap rounded-xl"
                    onClick={handleDownloadPDF}
                    disabled={downloading}
                    data-testid="download-pdf-btn"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    {downloading ? 'Opening...' : 'Download Guide'}
                  </Button>
                </div>
              </CardContent>
            </div>
          </Card>

          {/* Tabs */}
          <Tabs defaultValue="guides" className="mb-12">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8" data-testid="tutorials-tabs">
              <TabsTrigger value="guides">Written Guides</TabsTrigger>
              <TabsTrigger value="videos">Video Tutorials</TabsTrigger>
            </TabsList>

            <TabsContent value="guides" data-testid="guides-tab">
              <div className="space-y-12">
                {tutorials.map((category, idx) => {
                  const Icon = category.icon;
                  return (
                    <div key={idx}>
                      <div className="flex items-center gap-3 mb-6">
                        <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center">
                          <Icon className="h-6 w-6 text-teal-600" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900 font-heading">{category.category}</h2>
                          <p className="text-sm text-slate-500">{category.items.length} tutorials</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {category.items.map((item, itemIdx) => (
                          <Card
                            key={itemIdx}
                            className="overflow-hidden hover:shadow-xl hover:border-teal-200 transition-all cursor-pointer group"
                            onClick={() => handleTutorialClick(idx, itemIdx)}
                            data-testid={`tutorial-${idx}-${itemIdx}`}
                          >
                            <div className="h-40 overflow-hidden">
                              <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <CardHeader className="pb-4">
                              <div className="flex items-start justify-between mb-1">
                                <CardTitle className="text-lg group-hover:text-teal-600 transition-colors">{item.title}</CardTitle>
                                <span className="flex items-center gap-1 text-xs bg-teal-50 text-teal-600 px-2 py-1 rounded-full">
                                  <Clock className="h-3 w-3" /> {item.duration}
                                </span>
                              </div>
                              <CardDescription className="mb-3">{item.description}</CardDescription>
                              <div className="flex items-center text-sm text-teal-600 font-medium group-hover:gap-2 transition-all">
                                Read Tutorial
                                <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                              </div>
                            </CardHeader>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="videos" data-testid="videos-tab">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {videoTutorials.map((video, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group" data-testid={`video-${idx}`}>
                    <div className="relative h-52 overflow-hidden">
                      <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/30 transition-colors">
                        <div className="h-16 w-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <PlayCircle className="h-10 w-10 text-teal-600" />
                        </div>
                      </div>
                      <span className="absolute bottom-3 right-3 bg-black/80 text-white text-xs font-medium px-2 py-1 rounded">{video.duration}</span>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-lg">{video.title}</CardTitle>
                      <CardDescription>{video.description}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {/* Quick Tips with image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden">
              <img src={IMAGES.notification} alt="Notifications" className="w-full h-full object-cover" />
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Quick Tips for Indian Schools</CardTitle>
                <CardDescription>Best practices from 10,000+ schools</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { title: 'Plan Around Exams', text: 'Block leave requests during board exam periods (Feb-March, Oct-Nov)' },
                    { title: 'Track Substitutes', text: 'Keep a substitute teacher roster for departments with high leave rates' },
                    { title: 'Balance Check First', text: 'Teachers should always check leave balance before applying' },
                    { title: 'Upload Documents', text: 'Attach medical certificates for sick leave beyond 2 days' },
                    { title: 'Use Notifications', text: 'Enable notifications to get instant updates on leave status' },
                    { title: 'Monthly Reports', text: 'Generate reports monthly for management review and planning' }
                  ].map((tip, i) => (
                    <div key={i} className="flex gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-sm text-slate-900">{tip.title}</h4>
                        <p className="text-sm text-slate-600">{tip.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tutorial Detail Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-0">
              {selectedTutorial && (
                <>
                  <div className="h-56 overflow-hidden">
                    <img src={selectedTutorial.image} alt={selectedTutorial.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <DialogHeader className="mb-6">
                      <DialogTitle className="text-2xl">{selectedTutorial.title}</DialogTitle>
                      <DialogDescription className="flex items-center gap-2">
                        <Clock className="h-4 w-4" /> {selectedTutorial.duration} read
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mb-6">
                      {selectedTutorial.steps.map((step, i) => (
                        <div key={i} className="flex gap-4 items-start" data-testid={`dialog-step-${i}`}>
                          <div className="h-8 w-8 rounded-full bg-teal-600 text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">
                            {i + 1}
                          </div>
                          <div className="flex-1 pb-4 border-b border-slate-100 last:border-0">
                            <h4 className="font-semibold text-slate-900 mb-1">{step.step}</h4>
                            <p className="text-slate-600 text-sm leading-relaxed">{step.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {selectedTutorial.tip && (
                      <div className="bg-teal-50 border border-teal-100 rounded-lg p-4 mb-6">
                        <p className="text-sm text-teal-800">
                          <strong>Pro Tip:</strong> {selectedTutorial.tip}
                        </p>
                      </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button>
                      <Button className="bg-teal-600 hover:bg-teal-500 text-white rounded-xl" onClick={handleDownloadPDF}>
                        <Download className="h-4 w-4 mr-2" /> Download Full Guide
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Tutorials;
